import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import {
    StatsLeadersPosFilters,
    StatsLeadersTeamsFilters
} from './components/filters';
import {
    Heading,
    BenchmarkingSwitch,
    DataTypeToggle,
    dataTypeOptions
} from './components/header';
import {
    CategoryNavItem,
    SeasonNavItem,
    RoundNavItem
} from './components/nav-items';
import { StatsLeadersTable } from './components/table';
import {
    BenchmarkingExplainer,
    BenchmarkingLegend
} from 'common/react/components/benchmarking';
import {
    statsLeadersBenchmarkingSelector,
    statsLeadersNavSelector,
    statsLeadersFiltersSelector,
    statsLeadersDataTypeSelector
} from 'store/modules/statspro/stats-leaders/selectors';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { POSITIONS } from './config/positions';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { FilterButton } from 'common/react/components/FilterButton';
import { ResponsiveRoundNavItem } from './components/nav-items/ResponsiveRoundNavItem';
import { getTranslation } from 'common/react/utils/translations';
import { initialiseNav } from 'common/store/modules/statspro/thunks/initialise-nav';
import { useSyncUrlParams } from 'common/react/hooks/use-sync-url-params';
import { useGetUrlParams } from '../../utils/use-get-url-params';
import { useHasChangedFromDefaults } from 'common/react/components/competition-nav/hooks/use-has-changed-from-defaults';
import { useResetToDefaults } from '../common/hooks/use-reset-to-defaults';
import { statsLeadersActions } from 'common/store/modules/statspro/stats-leaders';

const MAXIMUM_SELECTIONS = 5;
const COUNTER_THRESHOLD = 2;
const INITIAL_CATEGORY = 'Key Stats';

const NavWrapper = ({ children }) => (
    <div className="competition-nav competition-nav--no-margin-top filter-dropdown rounded-filter-wrapper">
        {children}
    </div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

/**
 * @param root0
 * @param root0.compseasonId
 * @param root0.competitionId
 * @param root0.competitionPid
 * @param root0.showBenchmarkingAflw
 * @param root0.counterThreshold
 * @param root0.maximumSelections
 * @param root0.title
 * @returns {React.JSX.Element} Component.
 */
export const StatsLeaders = ({
    compseasonId,
    competitionId,
    competitionPid,
    showBenchmarkingAflw,
    title,
    maximumSelections = MAXIMUM_SELECTIONS,
    counterThreshold = COUNTER_THRESHOLD
}) => {
    const dispatch = useDispatch();

    const params = useGetUrlParams();

    const initialValues = {
        competitionId,
        seasonId: compseasonId,
        roundId: params.roundId || -1,
        filters: {
            positions: params.positions,
            teams: params.teams
        }
    };

    const initialParams = {
        category: params.category,
        benchmarking: params.benchmarking,
        sortDirection: params.sortDirection,
        sortColumn: params.sortColumn,
        dataType: params.dataType
    };

    useEffect(() => {
        dispatch(
            initialiseNav({
                slice: 'stats-leaders',
                initialValues: {
                    ...initialValues,
                    initialParams
                }
            })
        );
    }, [dispatch]);

    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);
    const [resetFilterBtnStyles, setResetFilterBtnStyles] = useState(false);

    const benchmarking = useSelector(statsLeadersBenchmarkingSelector);
    const dataType = useSelector(statsLeadersDataTypeSelector);

    const nav = useSelector(statsLeadersNavSelector);

    useSyncUrlParams({
        selector: (state) => state.statspro.statsLeaders,
        mapToParams: (items) => ({
            roundId: items.nav.round.id,
            category: items.nav.category,
            seasonId: items.nav.season.id,
            sortColumn: items.sort.columnId,
            sortDirection: items.sort.sortDirection,
            positions: items.filters.positions,
            teams: items.filters.teams,
            benchmarking: items.benchmarking.isActive,
            dataType: items.dataType.type
        }),
        isDisabled: !window.location.pathname.includes('leaders')
    });

    const filters = useSelector(statsLeadersFiltersSelector);
    const options = useCompetitionNavigationOptions(nav);

    const windowSize = useWindowSizeV2();
    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const showBenchmarkingAFLWBool = showBenchmarkingAflw === 'true';
    const { WOMENS } = PULSE.app.common.CONSTANTS.PROVIDER_IDS;
    const isAFLW = competitionPid === WOMENS;

    const isAFLM =
        competitionPid === PULSE.app.common.CONSTANTS.PROVIDER_IDS.PREMIERSHIP;

    const showBenchmarking =
        (!isAFLW || (isAFLW && showBenchmarkingAFLWBool)) &&
        (dataType?.type === 'averages' ||
            dataType?.type === 'benchmarkedAverages');

    const statsLeadersRef = useRef();

    const { hasChangedFromDefaults } = useHasChangedFromDefaults({
        state: useSelector((state) => state.statspro.statsLeaders),
        initialValues: {
            competitionId,
            seasonId: compseasonId,
            roundId: -1,
            category: INITIAL_CATEGORY,
            benchmarking: false,
            dataType: 'totals'
        }
    });

    const resetToDefaults = useResetToDefaults({
        slice: 'stats-leaders',
        initialValues: {
            competitionId,
            seasonId: compseasonId,
            roundId: -1,
            filters: {
                positions: [],
                teams: []
            },
            initialParams: {
                category: INITIAL_CATEGORY,
                benchmarking: { isActive: 'false' },
                dataType: 'totals'
            }
        }
    });

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: {
            category: nav.category,
            season: nav.season,
            position: filters.positions.map((pos) => ({ id: pos })),
            filters: {
                teams: filters.teams
            },
            round: nav.round,
            dataType,
            benchmarking
        },
        options: {
            ...options,
            dataTypes: dataTypeOptions,
            positions: POSITIONS.map((pos) => ({
                value: pos.keys[0],
                label: pos.name
            }))
        },
        filtersFields: [
            'category',
            'season',
            'round',
            'position',
            'teams',
            'benchmarking',
            'dataType'
        ],
        filtersRef: statsLeadersRef
    });

    // Reset functions for multi-select filters
    const resetPositions = () => {
        dispatch(
            statsLeadersActions.setFilterItem({
                filterItem: 'positions',
                value: []
            })
        );
    };

    const resetTeams = () => {
        dispatch(
            statsLeadersActions.setFilterItem({
                filterItem: 'teams',
                value: []
            })
        );
    };

    return (
        <div ref={statsLeadersRef}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <CategoryNavItem
                            classes="fixtures-nav__comp-select"
                            title={title}
                            hideLabel={true}
                        />
                        {showResponsiveItems && (
                            <FilterButton
                                classes="fixtures-nav__filter-button"
                                onChange={() =>
                                    setAreFilterOptionsVisible((prev) => !prev)
                                }
                                areFilterOptionsVisible={
                                    areFilterOptionsVisible
                                }
                                resetFilterBtnStyles={resetFilterBtnStyles}
                                setResetFilterBtnStyles={
                                    setResetFilterBtnStyles
                                }
                                showFilterLabel={false}
                                showActiveCircle
                            />
                        )}
                    </StatsSection.NavItems>
                    {(!showResponsiveItems || areFilterOptionsVisible) && (
                        <StatsSection.NavFilterItems
                            title={title}
                            filterKeys={[
                                'seasons',
                                'rounds',
                                'positions',
                                'teams'
                            ]}
                            initialValues={initialValues}
                            hideReset={true}
                        >
                            <SeasonNavItem title={title} />
                            {showResponsiveItems ? (
                                <ResponsiveRoundNavItem
                                    selector={statsLeadersNavSelector}
                                    removeFinals
                                    hideLabel
                                />
                            ) : (
                                <RoundNavItem title={title} />
                            )}
                            <StatsLeadersPosFilters
                                title={title}
                                maximumSelections={parseInt(maximumSelections)}
                                counterThreshold={parseInt(counterThreshold)}
                                addOnResetCallback={() => resetPositions}
                            />
                            <StatsLeadersTeamsFilters
                                title={title}
                                maximumSelections={parseInt(maximumSelections)}
                                counterThreshold={parseInt(counterThreshold)}
                                addOnResetCallback={() => resetTeams}
                            />
                            {hasChangedFromDefaults && (
                                <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                    <button
                                        className="stats-leaders-filter-reset-button"
                                        onClick={resetToDefaults}
                                    >
                                        {getTranslation(
                                            'label.statsLeaders.filters.resetButtonText'
                                        )}
                                    </button>
                                </div>
                            )}
                        </StatsSection.NavFilterItems>
                    )}
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentHeader>
                        <StatsSection.ContentHeaderHeading>
                            <Heading />
                        </StatsSection.ContentHeaderHeading>
                        <StatsSection.ContentHeaderOptions>
                            {showBenchmarking && <BenchmarkingSwitch />}
                            <DataTypeToggle />
                        </StatsSection.ContentHeaderOptions>
                        <StatsSection.ContentHeaderBelow>
                            {benchmarking?.isActive && showBenchmarking && (
                                <BenchmarkingLegend />
                            )}
                            {benchmarking?.isActive && showBenchmarking && (
                                <BenchmarkingExplainer />
                            )}
                        </StatsSection.ContentHeaderBelow>
                    </StatsSection.ContentHeader>
                    <StatsSection.ContentBody>
                        <StatsLeadersTable isAFLW={isAFLW} isAFLM={isAFLM} />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

StatsLeaders.propTypes = {
    compseasonId: PropTypes.string.isRequired,
    competitionId: PropTypes.string.isRequired,
    competitionPid: PropTypes.string.isRequired,
    showBenchmarkingAflw: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    title: PropTypes.string,
    maximumSelections: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    counterThreshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
